/* eslint-disable node/prefer-global/process */
import type { BarometerSuccess } from '@autobid/strapi-integration/typescript/auction/barometerCar'
import type { QueryKey, UseQueryOptions } from '@tanstack/vue-query'
import type { MaybeRef } from 'vue'
import { getError } from '@autobid/strapi-integration/utils/getError'
import { useBarometerSocket } from '@autobid/ui/composables/car/sockets/useBarometerSocket'
import { generateImageSizesFromObject } from '@autobid/ui/utils/car/generateImageSizes'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, onMounted, unref } from 'vue'

// #region constants
const CACHE_TIME = 1000 * 60 * 60
// #endregion

// #region types

type Options
  = | {
    type: 'current'
    auctionId: MaybeRef<number | undefined>
  }
  | {
    type: 'item'
    carId: MaybeRef<number>
  }

type QueryKeyProps
  = | {
    type: 'current'
    auctionId?: MaybeRef<number | undefined>
  }
  | {
    type: 'item'
    carId?: MaybeRef<number>
  }
// #endregion

// #region internal functions

function prepareCarData(data: BarometerSuccess['data']['singleItem']) {
  return {
    ...data,
    item: {
      ...data.item,
      itemData: {
        ...data.item.itemData,
        imageGroups: generateImageSizesFromObject(
          'hd',
          data.item.itemData.imageGroups,
        ),
      },
    },
  }
}

function useFetchBarometerCar() {
  const { $trpc } = useNuxtApp()
  const { locale } = useI18n()

  const fetchBarometerCar = async (
    options: Options,
  ): Promise<BarometerSuccess['data']['singleItem'] | null> => {
    const result = await (async function () {
      if (options.type === 'item') {
        return await $trpc.auctions.getBarometerCar.query({ type: 'item', carId: unref(options.carId), lang: locale.value })
      }

      const auctionId = unref(options.auctionId)
      if (options.type === 'current' && auctionId) {
        return await $trpc.auctions.getBarometerCar.query({ type: 'current', auctionId, lang: locale.value })
      }

      return null
    }())

    if (!result) {
      if (options.type === 'current') {
        return null
      }

      throw createError({
        statusCode: 404,
        statusMessage: 'No response',
      })
    }

    if ('errors' in result) {
      const { message: statusMessage, status_code: statusCode }
        = result.errors[0]

      if (options.type === 'current' && statusCode === 404) {
        return null
      }

      throw createError({ statusCode, statusMessage })
    }

    if ('error_code' in result.data.singleItem.metadata) {
      const { message: statusMessage, error_code: statusCode }
        = result.data.singleItem.metadata

      if (options.type === 'current' && statusCode === 404)
        return null

      throw createError({ statusCode, statusMessage })
    }

    if (!result.data.singleItem.item) {
      if (options.type === 'current')
        return null

      throw createError({ statusCode: 404, statusMessage: 'No data' })
    }

    return prepareCarData(
      result.data.singleItem as BarometerSuccess['data']['singleItem'],
    )
  }

  return { fetchBarometerCar }
}
// #endregion

export function getBarometerQueryKey(options: QueryKeyProps) {
  if (options.type === 'current') {
    const auctionId = unref(options.auctionId)

    return auctionId !== undefined
      ? (['barometer-current', auctionId] as const)
      : (['barometer-current'] as const)
  }

  const carId = unref(options.carId)

  return carId !== undefined
    ? (['barometer', carId] as const)
    : (['barometer'] as const)
}

export function usePrefetchBarometerCar() {
  const { fetchBarometerCar } = useFetchBarometerCar()
  const queryClient = useQueryClient()
  const { init: initSockets } = useBarometerSocket()
  onMounted(initSockets)

  const prefetchBarometerCar = async (
    options: Options,
  ): Promise<ReturnType<typeof fetchBarometerCar>> => {
    return await queryClient.fetchQuery({
      queryKey: getBarometerQueryKey(options) as QueryKey,
      queryFn: async () => {
        try {
          return await fetchBarometerCar(options)
        }
        catch {
          return null
        }
      },
      staleTime: CACHE_TIME,
      ...(process.client ? { cacheTime: CACHE_TIME } : {}),
    })
  }

  return { prefetchBarometerCar }
}

export function useBarometerCar(props: Options, options?: UseQueryOptions<BarometerSuccess['data']['singleItem'] | null, Error>) {
  const { fetchBarometerCar } = useFetchBarometerCar()
  const { init: initSockets } = useBarometerSocket()
  onMounted(initSockets)

  return useQuery({
    queryKey: computed(() => getBarometerQueryKey(props)),
    queryFn: async () => {
      try {
        return await fetchBarometerCar(props)
      }
      catch (error) {
        const { message, code, handled } = getError(error)

        if (!handled) {
          throw createError({ statusCode: code, statusMessage: message })
        }

        return null
      }
    },
    staleTime: CACHE_TIME,
    ...(process.client ? { cacheTime: CACHE_TIME } : {}),
    refetchOnWindowFocus: false,
    ...(options || {}),
  })
}
